import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useScrollReveal } from '../../../hooks/useScrollReveal';
import { useSupabaseProducts } from '../../../hooks/useSupabaseProducts';
import { useCart } from '../../../context/CartContext';
import { productSlugUrl } from '../../../utils/slug';

const categoryLabels = {
  'todos': 'Todos',
  'alta-costura': 'Alta Costura',
  'capsula': 'Cápsula Essential',
};

const availabilityLabels = {
  'disponible': 'En stock',
  'encargo': 'Bajo encargo',
  'agotado': 'Agotado',
};

const availabilityStyles = {
  'disponible': 'bg-brand-sage text-brand-black',
  'encargo': 'bg-brand-black text-white',
  'agotado': 'bg-brand-gray-200 text-brand-gray-500',
};

export default function TiendaCatalog() {
  const { ref, isVisible, style } = useScrollReveal({ threshold: 0.1, distance: 36, duration: 900 });
  const navigate = useNavigate();
  const { addItem, setIsCartOpen } = useCart();
  const [activeFilter, setActiveFilter] = useState<'todos' | 'alta-costura' | 'capsula'>('todos');
  const { products: allProducts, loading } = useSupabaseProducts();

  const filteredProducts = activeFilter === 'todos'
    ? allProducts
    : allProducts.filter((p) => p.category === activeFilter);

  return (
    <section ref={ref} className="w-full bg-white py-16 md:py-24">
      <div className="px-6 lg:px-[7%]">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6" style={style}>
          <div>
            <span className="font-sans text-brand-gray-400 text-[11px] tracking-[0.2em] uppercase font-medium">
              Catálogo
            </span>
          </div>

          {/* Filters */}
          <div className="flex gap-2 flex-wrap">
            {(['todos', 'alta-costura', 'capsula'] as const).map((filter) => (
              <button
                key={filter}
                onClick={() => setActiveFilter(filter)}
                className={`font-sans text-sm px-5 py-2.5 rounded-full transition-all duration-300 whitespace-nowrap ${
                  activeFilter === filter
                    ? 'bg-brand-black text-white'
                    : 'bg-brand-gray-50 text-brand-gray-600 hover:text-brand-black border border-brand-gray-200'
                }`}
              >
                {categoryLabels[filter]}
              </button>
            ))}
          </div>
        </div>

        {/* Loading */}
        {loading && allProducts.length === 0 && (
          <div className="mt-16 flex items-center justify-center gap-2 text-brand-gray-400 text-sm">
            <i className="ri-loader-4-line animate-spin" />
            Cargando catálogo...
          </div>
        )}

        {/* Products Grid */}
        <div className="mt-12 md:mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 lg:gap-8">
          {filteredProducts.map((product, index) => (
            <div
              key={product.id}
              onClick={() => navigate(productSlugUrl(product))}
              className="group cursor-pointer"
              style={isVisible ? {
                opacity: 1,
                transform: 'translateY(0px)',
                transition: `opacity 900ms cubic-bezier(0.25, 0.46, 0.45, 0.94) ${200 + index * 100}ms, transform 900ms cubic-bezier(0.25, 0.46, 0.45, 0.94) ${200 + index * 100}ms`,
              } : {
                opacity: 0,
                transform: 'translateY(36px)',
                transition: `opacity 900ms cubic-bezier(0.25, 0.46, 0.45, 0.94) ${200 + index * 100}ms, transform 900ms cubic-bezier(0.25, 0.46, 0.45, 0.94) ${200 + index * 100}ms`,
                willChange: 'opacity, transform',
              }}
            >
              <div className="relative aspect-[3/4] rounded-[24px] overflow-hidden bg-brand-gray-50 border border-transparent group-hover:border-brand-gray-100 transition-colors duration-500">
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all duration-500" />

                <span
                  className={`absolute top-4 left-4 px-3 py-1.5 rounded-full font-sans text-[11px] tracking-wider uppercase font-medium transition-transform duration-500 group-hover:translate-y-0 ${availabilityStyles[product.availability]}`}
                >
                  {availabilityLabels[product.availability]}
                </span>

                <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-all duration-300 translate-y-2 group-hover:translate-y-0 flex gap-2">
                  {product.availability === 'disponible' && (product.stock ?? 9999) > 0 && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        addItem({
                          productId: product.id,
                          name: product.name,
                          price: product.price,
                          image: product.image,
                          category: product.category,
                        });
                        setIsCartOpen(true);
                      }}
                      className="w-10 h-10 rounded-full bg-brand-black flex items-center justify-center shadow-lg transition-transform duration-300 hover:scale-105"
                      title="Añadir a la cesta"
                    >
                      <i className="ri-add-line text-white text-sm" />
                    </button>
                  )}
                  <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-lg transition-transform duration-300 group-hover:scale-105">
                    <i className="ri-arrow-right-line text-brand-black text-sm" />
                  </div>
                </div>
              </div>

              <div className="mt-4 px-1">
                <span className="font-sans text-brand-gray-400 text-[11px] tracking-wider uppercase">
                  {product.collection}
                </span>
                <h3 className="font-serif text-brand-black text-lg font-medium mt-1 leading-snug group-hover:text-brand-gray-500 transition-colors duration-300">
                  {product.name}
                </h3>
                <p className="font-sans text-brand-black text-sm font-semibold mt-2 transition-all duration-300 group-hover:translate-x-1">
                  {product.price.toLocaleString('es-ES', {
                    style: 'currency',
                    currency: 'EUR',
                    minimumFractionDigits: 0,
                  })}
                </p>
                <div className="flex items-center gap-2 mt-1">
                  <p className="font-sans text-brand-gray-400 text-xs">
                    Entrega: {product.deliveryTime}
                  </p>
                  {product.availability === 'disponible' && (
                    <span className="font-sans text-brand-gray-400 text-[10px]">
                      {`${product.stock ?? '—'} en stock`}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {!loading && filteredProducts.length === 0 && (
          <div className="text-center py-20">
            <p className="font-sans text-brand-gray-400 text-base">
              No hay productos en esta categoría.
            </p>
          </div>
        )}
      </div>
    </section>
  );
}